import { Component, ElementRef, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { animate, style, transition, trigger } from '@angular/animations';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import InvestingEntityType from 'src/app/dashboard/models/InvestingEntityType.enum';
import { UserService } from 'src/app/services/shared/user.service';
import { TerraUtils } from 'src/app/shared/TerraUtils';
import { InvestingEntityMenuComponent } from './investing-entity-menu.component';
import { InvestingEntityShareService } from './services/investing-entity-share.service';

@Component({
  selector: 'terra-investing-entity-share',
  templateUrl: './investing-entity-share.component.html',
  styleUrls: ['./investing-entity-share.component.scss'],
  animations: [
    trigger('inOutPaneAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-100%)' }),
        animate(
          '250ms ease-in-out',
          style({ opacity: 1, transform: 'translateX(0)' })
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateX(0)' }),
        animate(
          '200ms ease-in-out',
          style({ opacity: 0, transform: 'translateX(-100%)' })
        ),
      ]),
    ]),
  ],
})
export class InvestingEntityShareComponent {
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger })
  trigger: MatAutocompleteTrigger;

  constructor(
    private readonly investingEntityShareService: InvestingEntityShareService,
    private readonly userService: UserService,
    private readonly _bottomSheet: MatBottomSheet
  ) {
    this.filteredEntities = this.search.valueChanges.pipe(
      startWith(null),
      map((entityName: string | null) =>
        entityName ? this._filter(entityName) : this.investingEntities.slice()
      )
    );
  }

  get entities() {
    return this.shareForm.get('entities').value || [];
  }

  get canShare(): boolean {
    if (this.investingEntities?.length > 1) {
      return (
        this.shareForm.valid && this.shareForm.get('entities').value?.length > 0
      );
    } else {
      return (
        this.shareForm.get('email').value && this.shareForm.get('email').valid
      );
    }
  }

  isMobile = TerraUtils.isMobileDevice();

  userDetails$ = this.userService.accountDetails$.pipe(
    tap((user) => {
      this.existingEmails.add(user.email);
    })
  );

  InvestingEntityType = InvestingEntityType;
  isSharing: boolean = false;
  isEditing: boolean = false;
  selectedTab: number = 0;

  private investingEntities = [];
  private existingEmails: Set<string> = new Set();

  investingEntities$ = this.investingEntityShareService.investingEntities$.pipe(
    tap((entities) => {
      this.investingEntities = entities;
    })
  );

  sharedEntities$ = this.investingEntityShareService.sharedEntities$.pipe(
    tap((entities) => {
      entities.forEach((entity) => {
        this.existingEmails.add(entity.user.email);
      });
    })
  );

  shareForm = new FormGroup({
    email: new FormControl('', [
      Validators.email,
      this.emailExistsValidator(this.existingEmails),
    ]),
    entities: new FormControl(
      [],
      [Validators.required, Validators.minLength(1)]
    ),
  });

  search = new FormControl('');
  filteredEntities: Observable<any>;

  avatarLetters(str: string = '', str2: string = ''): string {
    const [fw, sw] = `${str} ${str2}`
      .split(/\s+/)
      .map((word) => word.charAt(0));
    return sw ? `${fw}${sw}` : fw;
  }

  private emailExistsValidator(existingEmails: Set<string>) {
    return (control: FormControl) => {
      if (existingEmails.has(control.value) && !this.isEditing) {
        return { emailExists: true };
      }
      return null;
    };
  }

  remove(item: string): void {
    const entities = this.entities;
    const index = entities?.indexOf(item);

    if (index >= 0) {
      entities.splice(index, 1);
      this.shareForm.get('entities').setValue(entities);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.toggleSelection(event.option.viewValue);
    this.searchInput.nativeElement.value = '';
    this.search.setValue(null);
  }

  optionClicked(event: Event, item: any) {
    event.stopPropagation();
    this.toggleSelection(item);
  }

  toggleSelection(item) {
    const index = this.entities.indexOf(item);

    if (index >= 0) {
      this.entities.splice(index, 1);
      this.shareForm.get('entities').setValue(this.entities);
    } else {
      this.shareForm.get('entities').setValue([...this.entities, item]);
    }
  }

  private _filter(value: string = ''): string[] {
    const filterValue = value.toLowerCase();
    return this.investingEntities.filter((entity) =>
      entity.name.toLowerCase().includes(filterValue)
    );
  }

  share() {
    if (!this.investingEntities.length) {
      return;
    }

    const email = this.shareForm.get('email').value;
    const ids = [];
    if (this.investingEntities.length === 1) {
      const entity = this.investingEntities[0];
      ids.push(entity.id);
    } else {
      ids.push(...this.entities.map((entity) => entity.id));
    }

    this.investingEntityShareService.share(email, ids);
    this.clear();
  }

  edit(entity) {
    this.isEditing = true;

    const entityIds = entity.investingEntities.map((e) => e.id);
    const investingEntities = this.investingEntities.filter((e) =>
      entityIds.includes(e.id)
    );

    this.shareForm.get('email').setValue(entity.user.email);
    this.shareForm.get('entities').setValue(investingEntities);
    this.selectedTab = 1;
  }

  tabsAnimationDone() {
    if (this.selectedTab === 1) {
      this.trigger.openPanel();
      this.searchInput.nativeElement.focus();
    }
  }

  continue() {
    this.isSharing = true;
    this.selectedTab = 1;
  }

  update() {
    if (this.shareForm.invalid) {
      return;
    }

    const email = this.shareForm.get('email').value;
    const ids = this.shareForm
      .get('entities')
      .value?.map((entity) => entity.id);

    if (email && ids?.length) {
      this.investingEntityShareService.update(email, ids);
    }

    this.clear();
  }

  delete(email, name) {
    this.investingEntityShareService.confirm(email, name);
  }

  resend(email) {
    this.investingEntityShareService.resend(email);
  }

  back() {
    this.clear();
  }

  openMenu(entity): void {
    const menu = [
      { text: 'Remove access', icon: 'delete_outline', action: 'delete' },
    ];
    if (this.investingEntities?.length > 1) {
      menu.unshift({
        text: 'Resend invite',
        icon: 'mail_outline',
        action: 'resend',
      });
    }
    if (!entity.user.name) {
      menu.unshift({ text: 'Edit access', icon: 'edit', action: 'edit' });
    }

    this._bottomSheet
      .open(InvestingEntityMenuComponent, { data: { menu } })
      .afterDismissed()
      .subscribe((selectedOption) => {
        switch (selectedOption) {
          case 'delete':
            this.delete(entity.user.email, entity.user.name);
            break;
          case 'edit':
            this.edit(entity);
            break;
          case 'resend':
            this.resend(entity.user.email);
            break;
          default:
            break;
        }
      });
  }

  private clear() {
    this.isSharing = false;
    this.isEditing = false;
    this.selectedTab = 0;
    this.shareForm.reset();
  }
}
